import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import Editor, { type Monaco } from '@monaco-editor/react';
import { useRef } from 'react';
import { useSchemaStore } from '../../store/useSchemaStore';
import { useThemeStore } from '../../store/useThemeStore';

let languageRegistered = false;

function registerDbmlLanguage(monaco: Monaco) {
  if (languageRegistered) return;
  languageRegistered = true;

  monaco.languages.register({ id: 'dbml' });
  monaco.languages.setMonarchTokensProvider('dbml', {
    keywords: ['Table', 'Enum', 'Ref', 'TableGroup', 'Project', 'Note', 'indexes', 'as'],
    settingKeys: ['pk', 'unique', 'not null', 'null', 'increment', 'default', 'note', 'ref', 'primary key'],
    tokenizer: {
      root: [
        [/\/\/.*$/, 'comment'],
        [/\b(Table|Enum|Ref|TableGroup|Project|indexes|as)\b/, 'keyword'],
        [/\bNote\b(?=\s*:)/, 'keyword'],
        [/`[^`]*`/, 'string.backtick'],
        [/'[^']*'/, 'string'],
        [/"[^"]*"/, 'string'],
        [/\b(pk|unique|not null|null|increment)\b/, 'type'],
        [/[<>-]{1,2}(?=\s|$)/, 'operator'],
        [/[{}[\]]/, '@brackets'],
        [/\b\d+\b/, 'number'],
        [/[A-Za-z_][\w]*/, 'identifier'],
      ],
    },
  });
  monaco.editor.defineTheme('dbml-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: '4f46e5', fontStyle: 'bold' },
      { token: 'type', foreground: '0d9488' },
      { token: 'string', foreground: 'b45309' },
      { token: 'string.backtick', foreground: 'b45309' },
      { token: 'comment', foreground: '9ca3af', fontStyle: 'italic' },
      { token: 'operator', foreground: 'dc2626' },
    ],
    colors: {},
  });
  monaco.editor.defineTheme('dbml-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: 'a5b4fc', fontStyle: 'bold' },
      { token: 'type', foreground: '5eead4' },
      { token: 'string', foreground: 'fbbf24' },
      { token: 'string.backtick', foreground: 'fbbf24' },
      { token: 'comment', foreground: '6b7280', fontStyle: 'italic' },
      { token: 'operator', foreground: 'f87171' },
    ],
    colors: {
      'editor.background': '#111827',
    },
  });
}

export default function DbmlEditor() {
  const readOnly = useDiagramViewStore((s) => s.readOnly);
  const dbmlSource = useSchemaStore((s) => s.dbmlSource);
  const parseErrors = useSchemaStore((s) => s.parseErrors);
  const setDbmlSource = useSchemaStore((s) => s.setDbmlSource);
  const theme = useThemeStore((s) => s.theme);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<Monaco | null>(null);

  function handleChange(value: string | undefined) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const text = value ?? '';
    debounceRef.current = setTimeout(() => setDbmlSource(text), 300);
  }

  function handleMount(editor: any, monaco: Monaco) {
    editorRef.current = editor;
    monacoRef.current = monaco;
  }

  const model = editorRef.current?.getModel?.();
  if (model && monacoRef.current) {
    monacoRef.current.editor.setModelMarkers(
      model,
      'dbml',
      parseErrors.map((err) => ({
        message: err.message,
        severity: monacoRef.current!.MarkerSeverity.Error,
        startLineNumber: err.line,
        endLineNumber: err.line,
        startColumn: err.column,
        endColumn: err.column + (err.length ?? 5),
      })),
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Editor
        height="100%"
        defaultLanguage="dbml"
        language="dbml"
        theme={theme === 'dark' ? 'dbml-dark' : 'dbml-light'}
        value={dbmlSource}
        onChange={handleChange}
        beforeMount={registerDbmlLanguage}
        onMount={handleMount}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 13,
          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          tabSize: 2,
        }}
      />
      {parseErrors.length > 0 && (
        <div className="max-h-28 overflow-y-auto border-t border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          {parseErrors.map((err, i) => (
            <div key={i}>
              Line {err.line}: {err.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
